import {
  Checkbox,
  FormControlLabel,
  InputBase,
  inputBaseClasses,
  Radio,
  RadioGroup,
  styled,
} from '@mui/material';
import classnames from 'classnames';
import { CodeField } from 'js/components/elements/CodeField';
import DateTimeField from 'js/components/elements/DateTimeField';
import Button from 'js/components/elements/editors/mui/Button';
import FileField from 'js/components/elements/FileField';
import FrequencyField from 'js/components/elements/FrequencyField';
import debouncedInputComponent from 'js/components/elements/mui/DebouncedInputComponent';
import Tooltip, { DefaultTooltipPopperProps } from 'js/components/elements/mui/Tooltip';
import {
  getCurrentSelectedOption,
  getNewSelectionValue,
  redesignStyles,
  SelectInput,
  SelectOption,
  SelectValue,
} from 'js/components/elements/rselect/ReactSelectUtils';
import SortedSelect from 'js/components/elements/rselect/SortedSelect';
import UnsortedSelect from 'js/components/elements/rselect/UnsortedSelect';
import DropdownWithSections from 'js/components/data_sources/DropdownWithSections';
import has from 'lodash/has';
import isArray from 'lodash/isArray';
import isBoolean from 'lodash/isBoolean';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import isPlainObject from 'lodash/isPlainObject';
import isUndefined from 'lodash/isUndefined';
import map from 'lodash/map';
import omit from 'lodash/omit';
import { MarkdownContent } from 'new/elements/MarkdownContent';
import { ReactComponent as InfoIcon } from 'new/components/icons/fi-rr-info.svg';
import { TextField } from 'new/elements/TextField';
import { SelectWithDependencies } from 'new/elements/SelectWithDependencies';
import React from 'react';
import styles from './FieldBuilder.module.scss';

const muiStyles = {
  buttonStyle: {
    width: '100%',
    marginBottom: 8,
    height: 36,
  },
  buttonLabelStyle: {
    fontSize: 10,
  },
};

function CustomField(props) {
  const wrappedLabel = (
    <HeaderField
      {...props}
      className={props.labelClassName}
      isRequired={props.isRequired}
      showRequiredIndicator={props.showRequiredIndicator}
      item={{
        name: props.id,
        display_name: props.label,
        description: props.description,
      }}
    />
  );

  return (
    <div data-testid={props['data-testid']} aria-label="custom-field">
      {wrappedLabel}
      {props.control}
    </div>
  );
}

const StyledInputBase = styled(debouncedInputComponent(InputBase, { timeout: 200 }))(() => ({
  'label + &': { marginBottom: 10 },
  [`.${inputBaseClasses.input}`]: {
    position: 'relative',
    fontWeight: 400,
    padding: 6,
    fontSize: 16,
    borderRadius: 8,
    color: 'var(--color-greyscale-800)',
    backgroundColor: 'var(--color-greyscale-0)',
    border: '1px solid var(--color-greyscale-100)',
  },
  [`&.${inputBaseClasses.multiline}`]: {
    [`& .${inputBaseClasses.input}`]: {
      whiteSpace: 'pre-wrap',
      overflow: 'auto',
      textOverflow: 'clip',
    },
  },
}));

function CustomTextField(props) {
  const textFieldProps = omit(props, [
    'name',
    'label',
    'description',
    'isRequired',
    'showRequiredIndicator',
    'labelClassName',
    'data-testid',
  ]);

  const control = (
    <StyledInputBase
      fullWidth
      margin="dense"
      name={props.name}
      inputProps={{ autoComplete: 'new-password' }}
      {...textFieldProps}
    />
  );

  return (
    <CustomField
      id={props.id}
      data-testid={props['data-testid']}
      label={props.label}
      description={props.description ?? null}
      labelClassName={props.labelClassName}
      isRequired={props.isRequired}
      showRequiredIndicator={props.showRequiredIndicator}
      control={control}
    />
  );
}

const noOptionsMessage = () => "Don't see the value you want? Type and press enter to add it.";

export function FieldBuilderRedesigned(props) {
  const {
    field,
    currentFieldValue,
    labeled = true,
    isRequired = false,
    showRequiredIndicator = true,
    id,
    onChange,
    fieldLabelClassName = '',
    skinnyFrequencyField = true,
    inModal,
    currentValues = {},
    onButtonFieldClicked,
    ...rest
  } = props;

  const { inputContainerClassname } = props;

  // Use document.body as portal target to prevent clipping
  const dropdownPortalTarget = typeof window !== 'undefined' ? document.body : undefined;

  const label = labeled ? (
    <HeaderField
      item={field}
      isRequired={isRequired}
      className={fieldLabelClassName}
      showRequiredIndicator={showRequiredIndicator}
    />
  ) : null;

  let fieldInputContent;

  const disabledField = Boolean(props?.disabled || field.disabled);

  const handleFieldChange = (fieldName, value) => {
    // Call onChange with the old API signature to maintain compatibility
    onChange(fieldName, value);
  };

  const onChangeField = (field) => (newValue) => {
    const value = (() => {
      if (field.inputType === 'select' || field.inputType === 'select-creatable') {
        return getNewSelectionValue(newValue, true);
      }
      if (newValue?.target) {
        return newValue.target.value;
      }
      return newValue;
    })();
    if (field.onChange) {
      field.onChange(value);
    }
    onChange(field.name, value);
  };

  if (field.type === 'ai_function') {
    const { currentValues } = props;

    return (
      <div
        className={classnames(styles.inputBaseContainer, inputContainerClassname)}
        key={`grp-${field.name}`}
      >
        {label}
        <DropdownWithSections
          field={field}
          sourceConfig={currentValues}
          onSourceConfigChanged={(newSourceConfig) => {
            // Update all fields that changed
            Object.keys(newSourceConfig).forEach((fieldName) => {
              if (currentValues[fieldName] !== newSourceConfig[fieldName]) {
                onChange(fieldName, newSourceConfig[fieldName]);
              }
            });
          }}
          onFieldUpdated={(fieldName, value) => {
            onChange(fieldName, value);
          }}
        />
      </div>
    );
  }

  switch (field.inputType) {
    case 'file':
      fieldInputContent = (
        <FileField
          data-testid={`file_${field.name}`}
          label={field.display_name}
          description={field.description}
          fileTypes={field.file_types}
          isBinary={field.is_binary}
          buttonType="primary"
          buttonStyle={{ ...muiStyles.buttonStyle, ...field.style }}
          buttonLabelStyle={muiStyles.buttonLabelStyle}
          onChange={onChangeField(field)}
        />
      );

      break;

    case 'button':
      fieldInputContent = (
        <Button
          data-testid={`button_${field.name}`}
          buttonTheme={'primary'}
          rootStyle={{ ...muiStyles.buttonStyle, ...field.style }}
          onClick={() => {
            if (onButtonFieldClicked) {
              onButtonFieldClicked(field.name, field.value, {
                ...currentValues,
                [field.name]: field.value,
              });
            } else {
              // Pass true as third argument to indicate this is a button field click
              onChange(field.name, field.value, true);
            }
          }}
        >
          <span style={muiStyles.buttonLabelStyle}>{field.display_name || 'Fetch Token'}</span>
        </Button>
      );

      break;

    case 'frequency':
    case 'frequency-type':
      fieldInputContent = (
        <FrequencyField
          data-testid={`frequency_${field.name}`}
          label={field.display_name}
          value={currentFieldValue}
          skinnyDisplay={skinnyFrequencyField}
          limitOptions={has(field, 'values') ? field.values : null}
          labelClassName={fieldLabelClassName}
          iconColor="#FFFFFF"
          labelStyle={{ fontWeight: 'normal', color: 'white' }}
          onChange={onChangeField(field)}
        />
      );

      break;

    case 'code':
      fieldInputContent = (
        <CodeField
          data-testid={`code_${field.name}`}
          label={field.display_name}
          value={currentFieldValue}
          onChange={onChangeField(field)}
          {...field.fieldProps}
        />
      );

      break;

    case 'datetime':
      fieldInputContent = (
        <DateTimeField
          data-testid={`date_time_${field.name}`}
          label={field.display_name}
          description={field.description ?? null}
          labelClassName={fieldLabelClassName}
          value={currentFieldValue}
          onChange={onChangeField(field)}
        />
      );

      break;

    case 'select':
      {
        let selectionValues = null;

        if (field.preserveValues) {
          selectionValues = field.values;
        } else {
          selectionValues =
            field.options ||
            field.values?.map((val) =>
              isPlainObject(val)
                ? val
                : {
                    label: val,
                    value: val,
                  },
            );
        }
        //TODO: Why do we need isPlainObject check?
        let currentSelectionValue = !isUndefined(currentFieldValue) ? currentFieldValue : null;

        if (isArray(currentSelectionValue)) {
          currentSelectionValue = map(currentSelectionValue, (value) =>
            getCurrentSelectedOption(selectionValues, value),
          );
        } else if (!isEmpty(currentSelectionValue) && !isPlainObject(currentSelectionValue)) {
          currentSelectionValue = getCurrentSelectedOption(selectionValues, currentFieldValue);
        }

        const SelectComponent = field.unsorted ? UnsortedSelect : SortedSelect;

        fieldInputContent = (
          <div
            data-testid={`select_${field.name}`}
            className={classnames(styles.inputBaseContainer, inputContainerClassname)}
          >
            {label}
            <SelectComponent
              id={id}
              variant="default"
              className={`field-${id}`}
              options={selectionValues}
              components={{
                Input: SelectInput,
                Option: SelectOption,
                SingleValue: SelectValue,
              }}
              aria-label={field.display_name}
              isClearable={false}
              isDisabled={disabledField}
              backspaceRemovesValue={false}
              value={currentSelectionValue}
              onChange={onChangeField(field)}
              isMulti={field.isMulti}
              menuPortalTarget={dropdownPortalTarget}
              styles={redesignStyles}
            />
          </div>
        );
      }
      break;

    case 'select-creatable':
      {
        let selectionValues;

        if (field.preserveValues) {
          selectionValues = field.values;
        } else {
          selectionValues = field.values.map((val) =>
            isPlainObject(val)
              ? val
              : {
                  label: val,
                  value: val,
                },
          );
        }
        //TODO: Why do we need isPlainObject check?
        let currentSelectionValue = !isUndefined(currentFieldValue) ? currentFieldValue : null;

        if (isArray(currentSelectionValue)) {
          currentSelectionValue = map(currentSelectionValue, (value) =>
            getCurrentSelectedOption(selectionValues, value),
          );
        } else if (!isEmpty(currentSelectionValue) && !isPlainObject(currentSelectionValue)) {
          currentSelectionValue = getCurrentSelectedOption(selectionValues, currentFieldValue);

          //handle new entry added that was not part of original options
          if (!isUndefined(currentFieldValue) && currentSelectionValue === null) {
            selectionValues.push({
              label: currentFieldValue,
              value: currentFieldValue,
            });
            currentSelectionValue = {
              label: currentFieldValue,
              value: currentFieldValue,
            };
          }
        }

        fieldInputContent = (
          <div
            data-testid={`editable_select_${field.name}`}
            className={classnames(styles.inputBaseContainer, inputContainerClassname)}
          >
            {label}
            <SortedSelect
              id={id}
              variant="default"
              className={`field-${id}`}
              data-testid={`editable_select_${field.name}`}
              options={selectionValues}
              placeholder="Select or type value..."
              noOptionsMessage={noOptionsMessage}
              components={{ Input: SelectInput, Option: SelectOption }}
              isClearable={true}
              isCreatable={true}
              isDisabled={disabledField}
              backspaceRemovesValue={false}
              value={currentSelectionValue}
              onChange={onChangeField(field)}
              isMulti={field.isMulti}
              menuPortalTarget={dropdownPortalTarget}
              styles={redesignStyles}
            />
          </div>
        );
      }
      break;

    case 'select-with-dependencies':
      {
        // This new input type uses the SelectWithDependencies component
        // It requires allFields to be passed as a prop to identify dependencies
        const { allFields } = props;

        if (!allFields) {
          // Fallback to regular select if allFields is not provided
          fieldInputContent = (
            <div
              data-testid={`select_${field.name}`}
              className={classnames(styles.inputBaseContainer, inputContainerClassname)}
            >
              {label}
              <SortedSelect
                id={id}
                options={field.values || []}
                value={currentFieldValue}
                onChange={onChangeField(field)}
                isDisabled={disabledField}
                isMulti={field.isMulti}
              />
            </div>
          );
        } else {
          fieldInputContent = (
            <SelectWithDependencies
              field={field}
              allFields={allFields}
              currentValues={props.currentValues || {}}
              onChange={onChange}
              currentFieldValue={currentFieldValue}
              id={id}
              disabled={disabledField}
              className={inputContainerClassname}
            />
          );
        }
      }
      break;

    case 'radio': {
      const radioOptions =
        field.options ||
        field.values?.map((val) =>
          isPlainObject(val) ? val : { label: String(val), value: val },
        ) ||
        [];
      const currentRadioValue = isNil(currentFieldValue) ? '' : String(currentFieldValue);

      fieldInputContent = (
        <div
          data-testid={`radio_${field.name}`}
          className={classnames(styles.inputBaseContainer, inputContainerClassname)}
          style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}
        >
          {label}
          <RadioGroup
            row
            id={id}
            aria-label={field.display_name}
            value={currentRadioValue}
            onChange={(event) => onChange(field.name, event.target.value)}
          >
            {radioOptions.map((opt) => (
              <FormControlLabel
                key={String(opt.value)}
                value={String(opt.value)}
                disabled={disabledField}
                control={<Radio size="small" />}
                label={opt.label}
              />
            ))}
          </RadioGroup>
        </div>
      );

      break;
    }

    case 'checkbox':
    case 'boolean': {
      let checkboxStatus = false;
      if (!isNil(currentFieldValue)) {
        checkboxStatus =
          isBoolean(currentFieldValue) || currentFieldValue === 'true' ? currentFieldValue : false;
      }

      const { inputContainerClassname } = props;

      fieldInputContent = (
        <div
          onClick={() => onChange(field.name, !checkboxStatus)}
          className={classnames(styles.checkBoxBaseContainer, inputContainerClassname)}
        >
          <Checkbox
            id={id}
            data-testid={`checkbox_${field.name}`}
            required={isRequired}
            checked={checkboxStatus}
            onChange={(_, checked) => onChange(field.name, checked)}
          />

          {label}
        </div>
      );

      break;
    }

    case 'textarea':
      fieldInputContent = (
        <CustomTextField
          data-testid={`textarea_${field.name}`}
          id={id}
          name={'form-text' + field.name}
          label={field.display_name}
          description={field.description ?? null}
          labelClassName={fieldLabelClassName}
          isRequired={isRequired}
          showRequiredIndicator={showRequiredIndicator}
          disabled={disabledField}
          multiline={true}
          minRows={4}
          maxRows={5}
          value={currentFieldValue !== null ? currentFieldValue : ''}
          style={field.style}
          onChange={onChangeField(field)}
        />
      );

      break;

    case 'password':
    case 'text':
    default:
      const elementKey = 'form-text' + field.name;
      const type = 'password' === field.inputType ? 'password' : 'text';

      fieldInputContent = (
        <div
          data-testid={`input_container_${field.name}`}
          key={elementKey}
          className={classnames(styles.inputBaseContainer, inputContainerClassname)}
        >
          {label}
          <TextField
            type={type}
            htmlFor={id}
            placeholder={field.placeholder || 'Enter a value'}
            data-testid={`input_${field.name}`}
            isRequired={isRequired}
            value={currentFieldValue ?? ''}
            disabled={disabledField}
            autoComplete="new-password"
            onChange={(event) => {
              onChange(field.name, event.target.value);
            }}
            {...field.fieldProps}
          />
        </div>
      );

      break;
  }

  return fieldInputContent;
}

export function HeaderField({
  item,
  className = '',
  isRequired = false,
  showRequiredIndicator = false,
}) {
  const title = item?.display_name + (showRequiredIndicator && isRequired ? ' *' : '');

  if (!item.description) {
    return (
      <label htmlFor={item?.name} className={classnames(styles.center_icon, className)}>
        {title}
      </label>
    );
  }

  return (
    <div className={classnames(styles.center_icon)}>
      <label htmlFor={item?.name} className={className}>
        {title}
      </label>
      <Tooltip
        interactive="true"
        placement="right"
        rootStyle={{}}
        arrow={false}
        tooltipTheme="fieldInfo"
        PopperProps={DefaultTooltipPopperProps}
        title={<MarkdownContent>{item.description}</MarkdownContent>}
        children={
          <div style={{ marginTop: 'var(--spacing-sm)' }}>
            <InfoIcon />
          </div>
        }
      />
    </div>
  );
}
